import { hourDraftFactory } from 'factories/hours';
import { merge, uniq, xor } from 'lodash-es';
import { normalize, schema } from 'normalizr';
import { xhrStatusFactory } from 'redux/reusable/xhrStatus';
import { HOUR_SCHEMA_KEY, hourListSchema, hourSchema, PROJECT_SCHEMA_KEY } from 'schema/hours';
import type { RawAudit, RawHourAuditChanges } from 'types/audits';
import type { Hour, HourDraft } from 'types/hour';
import type { Project } from 'types/project';
import { reducerWithInitialState } from 'typescript-fsa-reducers';
import { timestamp } from 'utils';
import type { DefaultEntities, State, UpdateDraftFromHourProps } from './hours.types';
import * as types from './hours.types';
import { clearHourHistory, fetchedHourHistory, hoursXhrStatusModule } from './hours.types';

const hourHistorySchema = new schema.Entity('hourHistory');

const hourHistoryArraySchema = new schema.Array(hourHistorySchema);

interface HourHistoryNormalized {
  entities: {
    hourHistory: {
      [id: number]: RawAudit<RawHourAuditChanges>;
    };
  };
  result: Array<number>;
}

const defaultEntities: DefaultEntities = {
  hour: {},
  [PROJECT_SCHEMA_KEY]: {},
};

const defaultHistoryState = {
  entities: {},
  result: [],
};

export const initialState: State = {
  draft: undefined,
  draftOriginal: undefined,
  batchDraft: undefined,
  entities: defaultEntities,
  result: [],
  loggedWeeklyTotal: null,
  history: defaultHistoryState,
  showHourHistoryModal: false,
  autoNoteStrategy: 'off',
  generatedNotes: null,
  xhrStatus: xhrStatusFactory(),
};

const reducer = reducerWithInitialState<State>(initialState)
  .case(fetchedHourHistory, (state, payload) => {
    const { entities, result }: HourHistoryNormalized = normalize(payload, hourHistoryArraySchema);

    return {
      ...state,
      history: {
        ...state.history,
        entities: entities.hourHistory,
        result,
      },
    };
  })
  .case(clearHourHistory, state => {
    return {
      ...state,
      history: initialState.history,
    };
  })

  .case(types.setHourDraft, (state, payload): State => {
    // If the hour exists, keep the original hour so we can revert optimistic updates if saving
    // fails and the user cancels the edit
    const draftOriginal = payload.id
      ? state.draftOriginal?.id === payload.id
        ? state.draftOriginal
        : state.entities.hour[payload.id]
      : undefined;

    return {
      ...state,
      draft: payload,
      draftOriginal,
    };
  })
  .case(types.unlockHourDraft, (state): State => {
    if (!state.draft) {
      return state;
    }
    return {
      ...state,
      draft: updateHourDraft(state.draft, {
        _unlockedByUser: true,
        locked_reason: null,
      }),
    };
  })
  .case(types.updateHourDraft, (state, payload): State => {
    if (!state.draft) {
      return state;
    }
    return {
      ...state,
      draft: updateHourDraft(state.draft, payload),
    };
  })
  .case(types.clearHourDraft, (state, payload): State => {
    return {
      ...state,
      xhrStatus: {
        ...state.xhrStatus,
        errors: [],
      },
      draft: undefined,
      draftOriginal:
        typeof payload !== 'undefined' && payload.preserveOriginal
          ? state.draftOriginal
          : undefined,
    };
  })
  .case(types.toggleHourHistoryModal, (state): State => {
    return {
      ...state,
      showHourHistoryModal: !state.showHourHistoryModal,
    };
  })
  .case(types.addEntryIdsOnHourDraft, (state, payload): State => {
    if (!state.draft) {
      return state;
    }
    return {
      ...state,
      draft: {
        ...state.draft,
        entry_ids: uniq([...payload.entry_ids, ...state.draft.entry_ids]),
      },
    };
  })
  .case(types.conditionallyRemoveEntryIdsOnHourDraft, (state, payload): State => {
    if (!state.draft) {
      return state;
    }
    // If we "Add to project" on a Memory/Group and choose the same project we've chosen
    // for the hour draft, don't make any changes.
    if (!state.draft.day || payload.ignoreForProjectId === state.draft.project) {
      return state;
    }

    console.info(
      payload.entry_ids,
      'entry ids removed from draft as they got predicted to another project'
    );

    return {
      ...state,
      draft: {
        ...state.draft,
        entry_ids: state.draft.entry_ids.filter(entryId => !payload.entry_ids.includes(entryId)),
      },
    };
  })
  .case(types.toggleEntryIdsOnHourDraft, (state, payload): State => {
    if (!state.draft) {
      return state;
    }

    const entryIds = xor(state.draft.entry_ids, payload.entry_ids);
    return {
      ...state,
      draft: {
        ...state.draft,
        entry_ids: entryIds,
      },
    };
  })
  .case(types.removeEntryIdsOnHourDraft, (state, payload): State => {
    if (!state.draft) {
      return state;
    }
    const draftEntryIds = state.draft.entry_ids || [];
    const entry_ids = draftEntryIds.filter(id => !payload.entry_ids.includes(id));
    return {
      ...state,
      draft: {
        ...state.draft,
        entry_ids,
      },
    };
  })
  .case(types.replaceHours, (state, payload): State => {
    const normalized = normalize<Hour<number>, DefaultEntities, Array<number>>(
      payload,
      hourListSchema
    );
    const updatedHours = {
      entities: { ...normalized.entities, project: state.entities.project },
      result: normalized.result,
    };

    return {
      ...state,
      ...updatedHours,
    };
  })
  .caseWithAction(types.fetchedHours, (state, { payload, meta }): State => {
    const normalized = normalize<Hour<Project>, DefaultEntities, Array<number>>(
      payload,
      hourListSchema
    );

    if (meta?.append) {
      return {
        ...state,
        entities: {
          ...state.entities,
          hour: {
            ...state.entities.hour,
            ...normalized.entities.hour,
          },
          project: {
            ...state.entities.project,
            ...normalized.entities.project,
          },
        },
      };
    } else {
      return {
        ...state,
        ...normalized,
      };
    }
  })
  .case(types.updateHours, (state, payload): State => {
    const hoursInState = new Set(state.result);
    const hoursToUpdate = payload.filter(h => hoursInState.has(h.id));
    const normalized = normalize<Hour<Project>, DefaultEntities, Array<number>>(
      hoursToUpdate,
      hourListSchema
    );

    const entitiesHour = normalized.entities.hour || state.entities.hour;
    const entitiesProject = normalized.entities.project || state.entities.project;

    return {
      ...state,
      entities: {
        hour: { ...state.entities.hour, ...entitiesHour },
        project: { ...state.entities.project, ...entitiesProject },
      },
      result: uniq([...state.result, ...normalized.result]),
    };
  })
  .case(types.injectHours, (state, payload): State => {
    const normalized = normalize<Hour<Project>, DefaultEntities, Array<number>>(
      payload,
      hourListSchema
    );
    const entitiesHour = normalized.entities.hour || state.entities.hour;
    const entitiesProject = normalized.entities.project || state.entities.project;

    // When editing a draft we can start a timer. If timer is stopped from mobile or other
    // platforms we need to update our draft with relevant timer props
    const hourMatchingDraft: Hour<number> | undefined = state.draft?.id
      ? entitiesHour[state.draft.id]
      : undefined;
    let draftOverrideFromUpdatedHour: Partial<UpdateDraftFromHourProps> = {};

    if (hourMatchingDraft) {
      const { timer_started_on, timer_stopped_on, timer_state, timestamps, updated_at } =
        hourMatchingDraft;

      draftOverrideFromUpdatedHour = {
        timer_started_on,
        timer_stopped_on,
        timer_state,
        timestamps,
        updated_at,
      };
    }

    return {
      ...state,
      entities: {
        hour: { ...state.entities.hour, ...entitiesHour },
        project: { ...state.entities.project, ...entitiesProject },
      },
      result: uniq([...state.result, ...normalized.result]),
      draft:
        state.draft != null
          ? {
              ...state.draft,
              ...draftOverrideFromUpdatedHour,
            }
          : state.draft,
    };
  })
  .case(types.clearHours, (state): State => {
    return {
      ...state,
      entities: defaultEntities,
      result: [],
    };
  })
  .case(types.addHour, (state, payload): State => {
    const normalized = normalize(payload, hourSchema);
    return {
      ...state,
      entities: merge({}, state.entities, normalized.entities),
      result: uniq([...state.result, normalized.result]),
    };
  })
  .case(types.editHour, (state, payload): State => {
    const normalized = normalize(payload, hourSchema);
    return {
      ...state,
      entities: {
        ...state.entities,
        hour: {
          ...state.entities.hour,
          ...normalized.entities.hour,
        },
        project: {
          ...state.entities.project,
          ...normalized.entities.project,
        },
      },
    };
  })
  .case(types.deleteHour, (state, payload): State => {
    const modifiedEntities = { ...state.entities[HOUR_SCHEMA_KEY] };
    delete modifiedEntities[payload];
    return {
      ...state,
      result: state.result.filter(entryId => entryId !== payload),
      entities: {
        ...state.entities,
        [HOUR_SCHEMA_KEY]: modifiedEntities,
      },
    };
  })
  .case(types.deleteHours, (state, payload): State => {
    const modifiedEntities = { ...state.entities[HOUR_SCHEMA_KEY] };

    payload.forEach(id => {
      if (modifiedEntities[id]) {
        delete modifiedEntities[id];
      }
    });
    return {
      ...state,
      result: state.result.filter(entryId => !payload.includes(entryId)),
      entities: {
        ...state.entities,
        [HOUR_SCHEMA_KEY]: modifiedEntities,
      },
    };
  })
  .case(types.setAutoNoteStrategy, (state, strategy): State => {
    return {
      ...state,
      autoNoteStrategy: strategy,
    };
  })
  .case(types.setLoggedWeeklyTotal, (state, payload): State => {
    if (payload === state.loggedWeeklyTotal) {
      return state;
    }

    return {
      ...state,
      loggedWeeklyTotal: payload,
    };
  })
  .case(types.batchCreateHourDraft, (state, { day, context }): State => {
    return {
      ...state,
      batchDraft: hourDraftFactory({ day }, context),
    };
  })
  .case(types.clearBatchHourDraft, (state): State => {
    return state.batchDraft
      ? {
          ...state,
          batchDraft: undefined,
        }
      : state;
  })
  .case(types.updateBatchHourDraft, (state, payload): State => {
    if (!state.batchDraft) {
      return state;
    }

    return {
      ...state,
      batchDraft: {
        ...state.batchDraft,
        ...payload,
      },
    };
  })
  .case(types.setGeneratedNotes, (state, payload): State => {
    return {
      ...state,
      generatedNotes: payload,
    };
  });

function updateHourDraft(base: HourDraft | undefined, changes: Partial<HourDraft>) {
  return base
    ? {
        ...base,
        ...changes,
        ...setLegacyTimestampField(changes),
        updated_at: timestamp(),
      }
    : base;
}

export default hoursXhrStatusModule.withXhrStatusReducer(reducer);

/**
 * The "from" and "to" should always be set to the value of the first timestamp.
 */
export function setLegacyTimestampField(
  changes: Partial<Pick<HourDraft, 'timestamps'>>
): Partial<Pick<HourDraft, 'from' | 'to'>> {
  if (changes.timestamps) {
    if (changes.timestamps.length === 0) {
      return { from: null, to: null };
    } else {
      const firstTimestamp = changes.timestamps[0];

      return {
        from: firstTimestamp.from,
        to: firstTimestamp.to,
      };
    }
  }

  return {};
}
